/**
 * ComparisonPage — SEO-optimized `/vs/:competitor` landing page.
 * Renders the FlavorFeed vs X comparison from `data.js`.
 *
 * Emits schema.org FAQPage + SoftwareApplication structured data inline
 * for Google rich results. Server-rendered markup (SSR) would rank faster,
 * but React lazy-hydrate still gets indexed by modern Googlebot.
 */
import React, { useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Check, X, ArrowRight, Star, Zap, Shield, Globe, TrendingUp } from 'lucide-react';
import { COMPARISONS } from './data';

const ComparisonPage = () => {
  const { competitor } = useParams();
  const data = COMPARISONS[competitor?.toLowerCase()];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [competitor]);

  if (!data) return <Navigate to="/" replace />;

  const canonical = `https://www.flavorfeed.ai/vs/${data.slug}`;

  // Schema.org: FAQPage + Product for rich results
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: data.faqs.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };
  const productSchema = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'FlavorFeed AI Restaurant POS',
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web, iOS, Android',
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-white">
      <Helmet>
        <title>{data.metaTitle}</title>
        <meta name="description" content={data.metaDescription} />
        <meta name="keywords" content={`${data.competitor} alternative, ${data.competitorShort} vs FlavorFeed, best ${data.competitorShort} alternative, restaurant POS comparison`} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={data.metaTitle} />
        <meta property="og:description" content={data.metaDescription} />
        <meta property="og:url" content={canonical} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.flavorfeed.ai/flavorfeed-logo-512.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={data.metaTitle} />
        <meta name="twitter:description" content={data.metaDescription} />
        <meta name="twitter:url" content={canonical} />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(productSchema)}</script>
      </Helmet>

      {/* Hero */}
      <section className="relative overflow-hidden px-6 pt-20 pb-16 sm:pt-28 sm:pb-24">
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(60%_80%_at_50%_0%,rgba(139,92,246,0.25),transparent)]" />
        <div className="mx-auto max-w-6xl">
          <nav className="mb-8 flex items-center gap-2 text-sm text-slate-400">
            <Link to="/" className="hover:text-white">Home</Link>
            <span>/</span>
            <span>Compare</span>
            <span>/</span>
            <span className="text-white">FlavorFeed vs {data.competitorShort}</span>
          </nav>
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-[1.2fr_1fr]">
            <div>
              <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-violet-500/40 bg-violet-500/10 px-3 py-1 text-xs font-medium text-violet-200" data-testid={`compare-${data.slug}-updated-ribbon`}>
                <Star className="h-3 w-3 fill-violet-300 text-violet-300" />
                Best {data.competitorShort} alternative · Updated Feb 2026
              </div>
              <h1 className="text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl">
                FlavorFeed AI <span className="text-slate-500">vs</span>{' '}
                <span className="bg-gradient-to-r from-violet-400 to-fuchsia-400 bg-clip-text text-transparent">
                  {data.competitor}
                </span>
              </h1>
              <p className="mt-6 text-lg text-slate-300 sm:text-xl">{data.tagline}</p>
              <ul className="mt-8 space-y-2.5">
                {data.heroBullets.map((b) => (
                  <li key={b} className="flex items-start gap-2.5 text-base text-slate-200">
                    <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-emerald-400" />
                    {b}
                  </li>
                ))}
              </ul>
              <div className="mt-10 flex flex-col gap-3 sm:flex-row">
                <Link
                  to="/signup?plan=free"
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-violet-600 to-fuchsia-600 px-6 py-3 text-base font-semibold shadow-lg shadow-violet-900/50 transition hover:from-violet-500 hover:to-fuchsia-500"
                  data-testid={`compare-${data.slug}-hero-cta`}
                >
                  Start Free in 2 Minutes <ArrowRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/demo"
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-700 bg-slate-800/60 px-6 py-3 text-base font-semibold text-slate-100 transition hover:border-slate-500 hover:bg-slate-800"
                >
                  Book 10-min demo
                </Link>
              </div>
              <p className="mt-3 text-[12px] text-slate-500">No credit card required · Free forever tier · Free migration from {data.competitorShort}</p>
            </div>

            {/* Right card: quick proof */}
            <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6 backdrop-blur">
              <h3 className="text-sm font-semibold text-slate-400">Typical monthly savings</h3>
              <div className="mt-1 text-4xl font-bold text-white">$180–$500</div>
              <p className="mt-1 text-sm text-slate-400">vs {data.competitorShort}, at $30K/month in card sales</p>
              <div className="my-6 h-px bg-slate-800" />
              <div className="space-y-4">
                <Metric icon={Zap} label="Onboarding" value="2 min → live" />
                <Metric icon={Shield} label="Contract" value="None. Cancel anytime." />
                <Metric icon={Globe} label="Countries" value="US + MENA + Global" />
                <Metric icon={TrendingUp} label="Typical payback" value="< 30 days" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="px-6 py-12">
        <div className="mx-auto max-w-4xl">
          <p className="text-lg leading-relaxed text-slate-300">{data.intro}</p>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="px-6 py-12">
        <div className="mx-auto max-w-5xl">
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">Full feature comparison</h2>
          <p className="mt-2 text-slate-400">Everything {data.competitor} offers, stacked next to FlavorFeed.</p>
          <div className="mt-8 overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/40">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-900/80 text-slate-400">
                <tr>
                  <th className="p-4 font-semibold">Feature</th>
                  <th className="p-4 font-semibold text-violet-300">FlavorFeed AI</th>
                  <th className="p-4 font-semibold text-slate-300">{data.competitorShort}</th>
                </tr>
              </thead>
              <tbody>
                {data.comparisonTable.map(([feat, us, them], i) => (
                  <tr
                    key={feat}
                    className={i % 2 === 0 ? 'bg-slate-900/30' : 'bg-transparent'}
                  >
                    <td className="p-4 font-medium text-slate-200">{feat}</td>
                    <td className="p-4 text-emerald-300">{us}</td>
                    <td className="p-4 text-slate-400">{them}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* When to pick which */}
      <section className="px-6 py-12">
        <div className="mx-auto max-w-5xl">
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">Which should you pick?</h2>
          <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2">
            <PickCard
              title="Pick FlavorFeed if…"
              items={data.whenToPick.flavorfeed}
              accent="violet"
            />
            <PickCard
              title={`Stay on ${data.competitorShort} if…`}
              items={data.whenToPick.competitor}
              accent="slate"
            />
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="px-6 py-12">
        <div className="mx-auto max-w-3xl">
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">Frequently asked</h2>
          <div className="mt-8 space-y-3">
            {data.faqs.map((f) => (
              <details key={f.q} className="group rounded-xl border border-slate-800 bg-slate-900/40 p-5">
                <summary className="cursor-pointer list-none font-semibold text-white marker:hidden">
                  <div className="flex items-start justify-between gap-4">
                    <span>{f.q}</span>
                    <span className="transition group-open:rotate-45 text-2xl leading-none text-slate-500">+</span>
                  </div>
                </summary>
                <p className="mt-3 text-slate-300 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="px-6 py-20">
        <div className="mx-auto max-w-3xl rounded-3xl border border-violet-500/40 bg-gradient-to-br from-violet-600/20 via-fuchsia-600/10 to-transparent p-10 text-center">
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">{data.ctaHeadline}</h2>
          <p className="mt-3 text-slate-300">Free forever. No credit card. No contract. Free migration from {data.competitorShort}.</p>
          <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
            <Link
              to="/signup?plan=free"
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-violet-600 to-fuchsia-600 px-7 py-3.5 text-base font-semibold shadow-lg shadow-violet-900/50 transition hover:from-violet-500 hover:to-fuchsia-500"
              data-testid={`compare-${data.slug}-final-cta`}
            >
              Start Free Now <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/"
              className="inline-flex items-center justify-center rounded-xl border border-slate-700 bg-slate-800/50 px-7 py-3.5 text-base font-semibold text-slate-100 transition hover:border-slate-500"
            >
              See all features
            </Link>
          </div>
        </div>
      </section>

      {/* Related comparisons */}
      <section className="border-t border-slate-800 px-6 py-10">
        <div className="mx-auto max-w-5xl">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-400">Related comparisons</h3>
          <div className="mt-4 flex flex-wrap gap-2">
            {Object.values(COMPARISONS)
              .filter((c) => c.slug !== data.slug)
              .map((c) => (
                <Link
                  key={c.slug}
                  to={`/vs/${c.slug}`}
                  className="rounded-full border border-slate-700 bg-slate-900/60 px-4 py-2 text-sm text-slate-200 transition hover:border-violet-500 hover:text-white"
                >
                  FlavorFeed vs {c.competitorShort} →
                </Link>
              ))}
          </div>
        </div>
      </section>
    </div>
  );
};

const Metric = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3">
    <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-violet-500/15 text-violet-300">
      <Icon className="h-4 w-4" />
    </div>
    <div>
      <div className="text-xs text-slate-400">{label}</div>
      <div className="text-sm font-semibold text-white">{value}</div>
    </div>
  </div>
);

const PickCard = ({ title, items, accent }) => {
  const accentCls =
    accent === 'violet'
      ? 'border-violet-500/40 bg-violet-500/5'
      : 'border-slate-700 bg-slate-900/40';
  const iconCls = accent === 'violet' ? 'text-emerald-400' : 'text-slate-500';
  const Icon = accent === 'violet' ? Check : X;
  return (
    <div className={`rounded-2xl border p-6 ${accentCls}`}>
      <h3 className="text-lg font-semibold text-white">{title}</h3>
      <ul className="mt-4 space-y-2.5">
        {items.map((item) => (
          <li key={item} className="flex items-start gap-2.5 text-sm text-slate-200">
            <Icon className={`mt-0.5 h-4 w-4 flex-shrink-0 ${iconCls}`} />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default ComparisonPage;
